/**
 * Albaranes.
 *
 * Se entra viendo TODOS. Durante un tiempo se entraba viendo sólo los que
 * faltaba firmar —lo que hay que cerrar antes de facturar con respaldo—, y
 * tenía sentido cuando los albaranes se hacían en la oficina y lo pendiente
 * era conseguir la firma.
 *
 * Con la app de campo el mundo cambió: llegan ya firmados, y la primera
 * pregunta de administración es «¿ha entrado lo de hoy?». Aquel filtro
 * respondía justo lo contrario y, lo peor, enseñaba CERO: parecía que no
 * había llegado nada. «Sin firmar» sigue a un clic, que es donde debe estar
 * un filtro y no un valor por omisión que engaña.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { day, dayOf } from '../table/format.js';

interface Row {
  id: string;
  number: string;
  status: string;
  delivered_on: string;
  customer_name: string;
  site_name: string | null;
  technician_name: string | null;
  signed_by_name: string | null;
  signed_at: string | null;
  travel_minutes: number | null;
  work_minutes: number | null;
}


const COLUMNS: Column<Row>[] = [
  {
    id: 'number',
    label: 'Nº',
    // El número abre la ficha: es donde se ve qué se firmó y el trazo.
    render: (r) => (
      <Link className="table__link" to={`/albaranes/${r.id}`}>
        {r.number}
      </Link>
    ),
    sortable: true,
    width: '10rem',
  },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name ?? '—' },
  { id: 'technicianName', label: 'Técnico', render: (r) => r.technician_name ?? 'Sin asignar' },
  {
    id: 'deliveredOn',
    label: 'Entregado',
    render: (r) => day(r.delivered_on),
    sortable: true,
    width: '8rem',
  },
  {
    id: 'signedAt',
    label: 'Firma',
    width: '14rem',
    render: (r) =>
      r.signed_at === null ? (
        <span className="pill pill--warn">⚠ Sin firmar</span>
      ) : (
        <span className="pill pill--ok">{`✓ ${r.signed_by_name ?? ''} · ${dayOf(r.signed_at)}`}</span>
      ),
    exportValue: (r) => (r.signed_at === null ? 'Sin firmar' : (r.signed_by_name ?? 'Firmado')),
  },
  {
    id: 'workMinutes',
    label: 'Trabajo (min)',
    numeric: true,
    render: (r) => r.work_minutes ?? '—',
    exportValue: (r) => r.work_minutes ?? '',
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'all', label: 'Todos', param: {} },
  { id: 'unsigned', label: 'Sin firmar', param: { unsigned: 'true' } },
] as const;

/**
 * El que se ve al entrar, y el único que NO deja parámetro en la URL.
 *
 * Se nombra una sola vez: cuando el defecto y el «cuál va sin parámetro»
 * vivían en dos sitios, cambiar uno y olvidar el otro dejaba la pestaña
 * marcada sin corresponderse con lo que se estaba viendo.
 */
const DEFECTO = QUICK[0].id;

export function DeliveryNotesScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/delivery-notes',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Albaranes</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === DEFECTO ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="button button--primary button--small" to="/albaranes/nuevo">
          Nuevo albarán
        </Link>
        {listing.total !== null && <span className="screen__count">{listing.total} albaranes</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="albaranes"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay albaranes que coincidan."
      />
    </section>
  );
}
